import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TextField } from '@mui/material';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import {
  CALENDAR_COLUMNS,
  CALENDAR_TITLE,
  COLUMN_HEADS,
  dayOption,
  formatClock,
  formatLength,
  LEADING_BLANKS,
  matchDays,
  matchTimes,
  parseTime,
  SELECTABLE_DAYS,
  timeOptions,
  TRAILING_BLANKS,
  type DayOption,
} from '../schedule/dateTime';

/**
 * The date and time fields on the personal event form.
 *
 * Both are the same control: a text box you type into, with a list underneath
 * that narrows as you type and can be walked with the arrow keys. Nothing is
 * committed until a suggestion is chosen, Enter is pressed, or focus leaves —
 * and what cannot be read is discarded rather than guessed at, so the field
 * always shows a real value.
 *
 * Modelled on Google Calendar's, for the reason that arrangement won: on a
 * form you fill in every week, typing "8:15p" is faster than any picker, and
 * the list is what stops that being a memory test.
 */

const Field = {
  '& .MuiOutlinedInput-root': {
    color: BRAND.parchment,
    fontFamily: FONTS.body,
    fontSize: 14,
    '& fieldset': { borderColor: 'rgba(243,238,231,0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(243,238,231,0.5)' },
    '&.Mui-focused fieldset': { borderColor: BRAND.parchment },
  },
  '& .MuiInputLabel-root': { color: 'rgba(243,238,231,0.6)', fontFamily: FONTS.body, fontSize: 14 },
  '& .MuiInputLabel-root.Mui-focused': { color: BRAND.parchment },
} as const;

const Wrap = styled('div')({ position: 'relative' });

/**
 * The suggestion list, over the fields below rather than pushing them down.
 *
 * A date row that reflows every time a field takes focus is a row whose other
 * fields move out from under the cursor.
 */
const List = styled('ul')({
  listStyle: 'none',
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  minWidth: '100%',
  zIndex: 30,
  margin: 0,
  padding: 4,
  maxHeight: 232,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  borderRadius: 8,
  background: '#1f1b18',
  border: '1px solid rgba(243,238,231,0.22)',
  boxShadow: '0 14px 30px rgba(0,0,0,0.5)',
});

const Choice = styled('li', { shouldForwardProp: (p) => p !== '$active' })<{ $active: boolean }>(
  ({ $active }) => ({
    padding: '7px 10px',
    borderRadius: 6,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    color: BRAND.parchment,
    fontFamily: FONTS.body,
    fontSize: 13.5,
    background: $active ? 'rgba(243,238,231,0.14)' : 'transparent',
    '&:hover': { background: 'rgba(243,238,231,0.14)' },
  }),
);

const Aside = styled('span')({ opacity: 0.55, marginLeft: 6 });

/**
 * The date popup: a month, not a list of sentences.
 *
 * Twenty-one dates read as a list are twenty-one lines to scan; read as a
 * calendar they are a shape you already know, where "the Wednesday of the
 * second week" is a position rather than a search. Typing still drives it —
 * what changes is that the answer is shown as a ring moving across the month
 * instead of a list narrowing to one row.
 */
const Calendar = styled('div')({
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  zIndex: 30,
  padding: '12px 12px 10px',
  borderRadius: 10,
  background: '#1f1b18',
  border: '1px solid rgba(243,238,231,0.22)',
  boxShadow: '0 14px 30px rgba(0,0,0,0.5)',
});

const MonthLabel = styled('div')({
  fontFamily: FONTS.serif,
  fontSize: 15,
  fontWeight: 700,
  color: BRAND.parchment,
  padding: '0 2px 8px',
});

const Grid = styled('div')({
  display: 'grid',
  gridTemplateColumns: `repeat(${CALENDAR_COLUMNS}, 32px)`,
  gap: 2,
});

const Head = styled('div')({
  display: 'grid',
  placeItems: 'center',
  height: 22,
  fontFamily: FONTS.body,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '0.04em',
  color: 'rgba(243,238,231,0.45)',
});

/**
 * One date.
 *
 * `$chosen` is the date the field holds; `$active` is the one the typing or
 * the arrow keys are pointing at, which is the same thing until either of them
 * says otherwise. Both are circles, because that is what a calendar uses, and
 * the committed one is the solid one.
 */
const Cell = styled('button', {
  shouldForwardProp: (p) => p !== '$active' && p !== '$chosen',
})<{ $active: boolean; $chosen: boolean }>(({ $active, $chosen }) => ({
  appearance: 'none',
  border: 'none',
  width: 32,
  height: 32,
  borderRadius: '50%',
  cursor: 'pointer',
  fontFamily: FONTS.body,
  fontSize: 13,
  fontWeight: $chosen ? 700 : 500,
  lineHeight: 1,
  color: $chosen ? BRAND.mocha : BRAND.parchment,
  background: $chosen
    ? BRAND.parchment
    : $active
      ? 'rgba(243,238,231,0.22)'
      : 'transparent',
  '&:hover': { background: $chosen ? BRAND.parchment : 'rgba(243,238,231,0.22)' },
}));

/** Text measured in the font it will actually be drawn in. */
let scratch: CanvasRenderingContext2D | null = null;

function textWidth(text: string, font: string): number {
  if (!scratch) scratch = document.createElement('canvas').getContext('2d');
  if (!scratch || !font) return text.length * 7.3;
  scratch.font = font;
  return scratch.measureText(text).width;
}

/**
 * Whether a given spelling of the date fits the box it has to sit in.
 *
 * The label has three lengths and choosing between them is a question about
 * this input, not about the window: the same panel is 495px beside a wide day
 * and the full width of a phone. So the input is measured, and so is the text
 * — estimating characters got "Monday, September 21" wrong by 12px, which is
 * the difference between the answer and the next answer down.
 */
function useFits(): [React.RefObject<HTMLInputElement | null>, (text: string) => boolean] {
  const ref = useRef<HTMLInputElement | null>(null);
  const [box, setBox] = useState({ room: 0, font: '' });

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    const read = () => {
      const style = getComputedStyle(node);
      setBox({
        font: `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`,
        room:
          node.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight),
      });
    };

    read();
    const observer = new ResizeObserver(read);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const fits = useCallback(
    (text: string) => box.room === 0 || textWidth(text, box.font) <= box.room,
    [box],
  );

  return [ref, fits];
}

/** The longest spelling that fits. */
function labelFor(day: DayOption | null, fits: (text: string) => boolean): string {
  if (!day) return '';
  if (fits(day.full)) return day.full;
  if (fits(day.medium)) return day.medium;
  return day.short;
}

interface ComboProps {
  label: string;
  /** What the field shows when it is not being typed into. */
  display: string;
  /** Rendered rows. `key` is React's; `text` is what a row reads. */
  options: { key: string; text: string; aside?: string; cell?: string }[];
  /** How the options are drawn: a list of times, or a month of dates. */
  layout?: 'list' | 'calendar';
  /**
   * Where the highlight should sit for what has been typed, or -1 to leave it
   * where it is. The calendar keeps every date on screen and moves the ring;
   * a list narrows instead, and always starts at the top.
   */
  preferredActive?: number;
  /** Index of the row matching the current value, for the initial highlight. */
  selected: number;
  /** The option key the field currently holds, drawn as the solid circle. */
  selectedKey?: string;
  onPick: (key: string) => void;
  /** Called with raw text when focus leaves: last chance to read it. */
  onCommit: (text: string) => void;
  onQuery: (text: string | null) => void;
  query: string | null;
  onOpen: () => void;
  flex: number;
  /**
   * The width below which this field stops sharing a row.
   *
   * Three fields across a phone leaves each of them too narrow to read the
   * value inside it; the row wraps instead, and the date takes a line of its
   * own — where it is wide enough to be spelled out in full again.
   */
  minWidth: number;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

function Combo({
  label,
  display,
  options,
  selected,
  selectedKey,
  onPick,
  onCommit,
  onQuery,
  query,
  onOpen,
  flex,
  minWidth,
  inputRef,
  layout = 'list',
  preferredActive = 0,
}: ComboProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const list = useRef<HTMLUListElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  /** True once the arrow keys have moved the highlight off what was typed. */
  const steered = useRef(false);

  useEffect(() => {
    if (open) setActive(selected < 0 ? 0 : selected);
    // Only when the list opens: re-running on every keystroke would fight the
    // arrow keys for control of the highlight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Typing moves the highlight to whatever it now points at.
  useEffect(() => {
    if (query == null || steered.current) return;
    if (preferredActive >= 0) setActive(preferredActive);
  }, [query, preferredActive]);

  // Keep the highlighted row in view, whether it moved by key or by opening.
  useEffect(() => {
    if (!open || layout === 'calendar') return;
    const node = list.current?.children[active] as HTMLElement | undefined;
    node?.scrollIntoView({ block: 'nearest' });
  }, [open, active, options.length, layout]);

  const close = useCallback(() => {
    setOpen(false);
    steered.current = false;
    onQuery(null);
  }, [onQuery]);

  const pick = useCallback(
    (key: string) => {
      onPick(key);
      setOpen(false);
      steered.current = false;
    },
    [onPick],
  );

  /**
   * What Enter or Tab means.
   *
   * Whatever the highlight is on, once the arrow keys have moved it — that is
   * the only reason to have moved it. Otherwise the typed text, which can say
   * something no row does: "9:37" is a real answer, and the row under the
   * highlight would be 9:30.
   */
  const commit = useCallback(
    (text: string) => {
      if (steered.current && options[active]) pick(options[active].key);
      else if (query != null && query.trim()) onCommit(text);
      else if (options[active]) pick(options[active].key);
      else onCommit(text);
      close();
    },
    [active, options, pick, onCommit, query, close],
  );

  return (
    <Wrap style={{ flex, minWidth }}>
      <TextField
        label={label}
        fullWidth
        inputRef={inputRef}
        value={query ?? display}
        autoComplete="off"
        onFocus={(e) => {
          onOpen();
          setOpen(true);
          e.target.select();
        }}
        onChange={(e) => {
          // Typing takes the highlight back: it belongs to the text again.
          steered.current = false;
          onQuery(e.target.value);
          setOpen(true);
        }}
        onBlur={(e) => {
          // A click on a suggestion blurs the field before it fires; the panel
          // handles its own pick, so only text needs settling here.
          const to = e.relatedTarget as Node | null;
          if (to && (list.current?.contains(to) || panel.current?.contains(to))) return;
          onCommit(e.target.value);
          close();
        }}
        onKeyDown={(e) => {
          const arrows = ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'];
          if (arrows.includes(e.key)) {
            // Sideways only means something on a calendar; in a list it is
            // the caret's, and taking it would break editing the text.
            if (layout !== 'calendar' && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) return;
            e.preventDefault();
            if (!open) {
              setOpen(true);
              return;
            }
            const week = layout === 'calendar' ? CALENDAR_COLUMNS : 1;
            const step =
              e.key === 'ArrowDown' ? week : e.key === 'ArrowUp' ? -week : e.key === 'ArrowRight' ? 1 : -1;
            steered.current = true;
            setActive((i) => Math.min(Math.max(i + step, 0), options.length - 1));
            return;
          }
          if (e.key === 'Enter') {
            // Never submits the form from here: Enter in a combo means "take
            // what I am looking at".
            e.preventDefault();
            e.stopPropagation();
            commit((e.target as HTMLInputElement).value);
            return;
          }
          if (e.key === 'Escape') {
            e.stopPropagation();
            close();
          }
          if (e.key === 'Tab') {
            commit((e.target as HTMLInputElement).value);
          }
        }}
        sx={Field}
        slotProps={{
          htmlInput: {
            role: 'combobox',
            'aria-expanded': open,
            'aria-autocomplete': 'list',
          },
        }}
      />

      {open && layout === 'calendar' && (
        <Calendar ref={panel} role="dialog" aria-label={label}>
          <MonthLabel>{CALENDAR_TITLE}</MonthLabel>
          <Grid aria-hidden="true">
            {COLUMN_HEADS.map((head, i) => (
              <Head key={`${head}${i}`}>{head}</Head>
            ))}
          </Grid>
          <Grid role="listbox" aria-label={label}>
            {Array.from({ length: LEADING_BLANKS }, (_, i) => <span key={`lead${i}`} />)}
            {options.map((option, i) => (
              <Cell
                key={option.key}
                type="button"
                role="option"
                tabIndex={-1}
                aria-selected={option.key === selectedKey}
                aria-label={option.text}
                $active={i === active}
                $chosen={option.key === selectedKey}
                // Ahead of blur, so the pick lands before the field settles.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(option.key);
                }}
                onMouseEnter={() => {
                  steered.current = true;
                  setActive(i);
                }}
              >
                {option.cell}
              </Cell>
            ))}
            {Array.from({ length: TRAILING_BLANKS }, (_, i) => <span key={`trail${i}`} />)}
          </Grid>
        </Calendar>
      )}

      {open && layout === 'list' && options.length > 0 && (
        <List ref={list} role="listbox" aria-label={label}>
          {options.map((option, i) => (
            <Choice
              key={option.key}
              role="option"
              aria-selected={i === active}
              $active={i === active}
              // Ahead of blur, so the pick lands before the field settles.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(option.key);
              }}
              onMouseEnter={() => setActive(i)}
            >
              {option.text}
              {option.aside && <Aside>{option.aside}</Aside>}
            </Choice>
          ))}
        </List>
      )}
    </Wrap>
  );
}

export function DateField({
  value,
  onChange,
  // A little over twice a time field's share of the row: enough that the
  // longest date in range, "Wednesday, September 30", is spelled out at the
  // form's usual width, with the shorter spellings held back for the widths
  // where they are genuinely needed.
  flex = 2.3,
}: {
  value: string;
  onChange: (dayKey: string) => void;
  flex?: number;
}) {
  const [query, setQuery] = useState<string | null>(null);
  const [ref, fits] = useFits();

  const day = dayOption(value);

  /**
   * Every date, always — the calendar shows the month whatever is typed.
   *
   * What typing moves is the highlight: the first date the text could mean,
   * which is the ring travelling to the 22nd as "9/22" is typed. Text that
   * means nothing leaves the ring where it was rather than emptying the month.
   */
  const options = useMemo(
    () =>
      SELECTABLE_DAYS.map((d) => ({ key: d.key, text: d.full, cell: String(d.dayOfMonth) })),
    [],
  );

  const selected = SELECTABLE_DAYS.findIndex((d) => d.key === value);
  const pointsAt = useMemo(() => {
    if (query == null) return selected;
    const hit = matchDays(query)[0];
    return hit ? SELECTABLE_DAYS.findIndex((d) => d.key === hit.key) : -1;
  }, [query, selected]);

  return (
    <Combo
      label="Date"
      layout="calendar"
      inputRef={ref}
      display={labelFor(day, fits)}
      options={options}
      selected={selected}
      selectedKey={value}
      preferredActive={pointsAt}
      onPick={(key) => {
        onChange(key);
        setQuery(null);
      }}
      onCommit={(text) => {
        // One candidate left is the one they meant, however partly they typed
        // it. Anything else keeps the date that was already there.
        const found = matchDays(text);
        if (text.trim() && found.length >= 1) onChange(found[0].key);
        setQuery(null);
      }}
      onQuery={setQuery}
      query={query}
      onOpen={() => setQuery('')}
      flex={flex}
      minWidth={190}
    />
  );
}

export function TimeField({
  label,
  value,
  onChange,
  /** Set on the end field: rows carry the length they would make. */
  from,
  flex = 1,
}: {
  label: string;
  value: number;
  onChange: (minutes: number) => void;
  from?: number;
  flex?: number;
}) {
  const [query, setQuery] = useState<string | null>(null);

  /**
   * The end field offers times from the start onwards — an end before the
   * beginning is not a thing anyone means — and the start field offers the
   * whole day.
   */
  const all = useMemo(() => timeOptions(from == null ? 0 : from + 1), [from]);
  const matches = useMemo(() => matchTimes(query ?? '', all), [query, all]);

  const options = useMemo(
    () =>
      matches.map((m) => ({
        key: String(m),
        text: formatClock(m),
        aside: from == null ? undefined : `(${formatLength(m - from)})`,
      })),
    [matches, from],
  );

  return (
    <Combo
      label={label}
      display={formatClock(value)}
      options={options}
      selected={matches.indexOf(value)}
      onPick={(key) => {
        onChange(Number(key));
        setQuery(null);
      }}
      onCommit={(text) => {
        const parsed = parseTime(text);
        if (parsed != null) onChange(parsed);
        setQuery(null);
      }}
      onQuery={setQuery}
      query={query}
      onOpen={() => setQuery('')}
      flex={flex}
      minWidth={96}
    />
  );
}
